/**
 * Procedural BATHROOM basin + faucet emitters for the manual cabinet-run
 * engine — the vanity twin of `kitchen-assembly/buildKitchenSink.js`, and the
 * same contract: `id` and `addedBy` are caller-supplied so the run keeps its
 * own id scheme (`sink_<bayId>` / `faucet_<bayId>`) and provenance tag. Pure.
 *
 * Emits the bathroom vocabulary — `type: 'sink'` with a canonical `sinkType`,
 * and a faucet via the shared `createSinkFaucet` (which emits
 * `bathroom-sink-faucet`) — so the existing sink/faucet renderers and the
 * countertop CSG cutout draw a run-built vanity exactly like a
 * `build_vanity` one.
 *
 * The basin is the procedural fallback shape, FITTED TO ITS BAY the same way
 * `assembleVanity`'s fallback basin fits its cabinet: width capped at
 * bay − 2×3" reveal, depth capped at cabinet depth − 2×1.25" margin
 * (`SINK_FIT_MARGINS`). Restyling to a catalog model stays a downstream
 * concern, as it is for every run-emitted object.
 */

const { getWallAxes } = require('../kitchen-assembly/wallMath');
const {
  VANITY_DIMENSIONS,
  SINK_FIT_MARGINS,
  SMALLEST_CATALOG_SINK_CUTOUT,
} = require('./constants');
const { buildSinkDimensions } = require('../sink-types');
const { calculateSinkSeatY } = require('./calculateSinkSeatY');
const {
  PROCEDURAL_BASIN_MODEL_ID,
  PROCEDURAL_BASIN_SINK_TYPE,
  proceduralBasinDimensions,
} = require('./proceduralBasin');
const { synthesizeOvalSinkCutout } = require('./sinkCutout');
const { createSinkFaucet } = require('./createSinkFaucet');

/**
 * Build a bathroom sink basin sunk into the run countertop above a vanity
 * sink cabinet. `range` is the sink cabinet's along-wall {start,end} param;
 * `cabinetDepth`/`cabinetHeight`/`counterThickness` come from the run's
 * dimension profile (plus any per-run depth override the cabinet was built
 * at). Returns null when the wall axes are unavailable.
 */
function buildVanitySinkBasin({
  id,
  wall,
  range,
  floorY,
  countertopId,
  sinkCabinetId,
  fixtureGroupId,
  addedBy,
  cabinetDepth,
  // The counter the bowl actually hangs from, when it overhangs the carcass
  // (`counterOverhang.js`). Absent or no deeper than the carcass → the
  // carcass is the bound, which is every ordinary vanity and is what this
  // builder has always done.
  counterDepth,
  cabinetHeight,
  counterThickness,
  sinkType = 'undermount',
}) {
  const axes = getWallAxes(wall);
  if (!axes) return null;
  const ry = axes.wallRotationY;
  const wallParam = (range.start + range.end) / 2;
  const bayWidth = Math.abs(range.end - range.start);

  // The fallback-basin fit rule for an UNDERMOUNT: standard 24"×18" basin,
  // shrunk to leave the cabinet face its reveal and the counter its margin.
  // Clamping to the cabinet is right because an undermount hangs INSIDE it —
  // it is not a universal rule. A vessel sits on top of the counter and an
  // apron projects past the cabinet face, so either would have to be seated
  // rather than shrunk (`calculateSinkSeatY` already models the vertical half
  // of that). This builder only ever emits `undermount`, which is why the
  // clamp is safe here today.
  let basinWidth = Math.min(
    VANITY_DIMENSIONS.sink.width,
    bayWidth - 2 * SINK_FIT_MARGINS.width
  );
  // THE BOWL HANGS FROM THE COUNTER, NOT THE CABINET (user ruling
  // 2026-09-25), so where the counter stands proud of a shallow carcass the
  // bowl is sized against the counter — that overhanging strip is exactly
  // what a console basin uses. The clamp to the cabinet stays the bound for
  // every ordinary vanity, which is what the note above describes.
  const bowlBound = Math.max(cabinetDepth, counterDepth || 0);
  let basinDepth = Math.min(
    VANITY_DIMENSIONS.sink.depth,
    bowlBound - 2 * SINK_FIT_MARGINS.depth
  );
  if (basinWidth <= 0 || basinDepth <= 0) return null;
  let basinHeight = VANITY_DIMENSIONS.sink.height;

  // NO CATALOG BOWL FITS THIS BOX — so draw ours, and say so.
  //
  // `SMALLEST_CATALOG_SINK_CUTOUT` is the shallowest undermount cutout in
  // `unifiedObjects` (12.81"), and a box that cannot clear it has no product
  // to host: dev's shallowest scanned carcass is 7.0", which even at the 6"
  // overhang cap leaves a 10.5" bowl. `vanityToRun` has warned about exactly
  // this since the overhang work; what it could not do was decide what to
  // build instead.
  //
  // The answer is the built-in basin, at the size `proceduralBasin.js` holds
  // for it — 19" x 16" x 7" shrunk to the box by its own reveals. Sizing it
  // from `VANITY_DIMENSIONS.sink` (24" x 18", the LAYOUT figure) instead is
  // what that module exists to prevent: "extracted so the builder and the
  // picker cannot answer differently", and the picker's Placeholder tile asks
  // for these numbers. Stamping the model id is the other half — the panel
  // then reports the built-in render as the applied model rather than calling
  // a drawn bowl a catalog undermount.
  const catalogCanFit = basinDepth >= SMALLEST_CATALOG_SINK_CUTOUT.depth - 1e-9;
  let modelId;
  let mount = sinkType;
  if (!catalogCanFit) {
    const drawn = proceduralBasinDimensions({
      width: bayWidth,
      depth: bowlBound,
    });
    if (!(drawn.width > 0) || !(drawn.depth > 0)) return null;
    basinWidth = drawn.width;
    basinDepth = drawn.depth;
    basinHeight = drawn.height;
    modelId = PROCEDURAL_BASIN_MODEL_ID;
    mount = PROCEDURAL_BASIN_SINK_TYPE;
  }

  const countertopTopY = floorY + cabinetHeight + counterThickness;
  const countertopBottomY = floorY + cabinetHeight;
  const seatY = calculateSinkSeatY({
    sinkType: mount,
    modelHeightMeters: basinHeight,
    countertopTopY,
    countertopBottomY,
  });

  // How far off the wall the bowl's centre sits. Ordinarily the carcass's own
  // midline; where the counter overhangs, the fit rule's own placement — one
  // back margin off the wall, then half the bowl — so the extra counter ends
  // up IN FRONT of the bowl rather than behind it.
  const bowlOffset =
    bowlBound > cabinetDepth + 1e-9
      ? SINK_FIT_MARGINS.depth + basinDepth / 2
      : cabinetDepth / 2;
  const center = {
    x:
      axes.start.x +
      wallParam * axes.wallDir.x +
      bowlOffset * axes.wallNormal.x,
    y: seatY ?? countertopBottomY - basinHeight / 2,
    z:
      axes.start.z +
      wallParam * axes.wallDir.z +
      bowlOffset * axes.wallNormal.z,
  };

  return {
    id,
    type: 'sink',
    sinkType: mount,
    ...(modelId ? { modelId } : {}),
    label: 'Sink',
    category: { sink: true },
    center,
    dimensions: buildSinkDimensions({
      width: basinWidth,
      height: basinHeight,
      depth: basinDepth,
      // The cut through the countertop follows the nominal basin.
      cutoutDepth: basinHeight,
    }),
    rotation: { x: 0, y: ry, z: 0 },
    parents: [
      { id: countertopId, type: 'countertop' },
      ...(sinkCabinetId ? [{ id: sinkCabinetId, type: 'lower-cabinet' }] : []),
    ],
    parentWallIds: [wall.id],
    ...(fixtureGroupId ? { fixtureGroupId } : {}),
    'sink-cutout': synthesizeOvalSinkCutout(basinWidth, basinDepth),
    addedBy,
    lastModified: new Date().toISOString(),
  };
}

/**
 * Build a bathroom faucet behind the basin, on the run countertop. Thin
 * wrapper over the shared `createSinkFaucet` so the setback/containment rules
 * are the vanity path's own — the run engine adds only its id scheme and the
 * counter-stack Y.
 */
function buildVanityFaucet({
  id,
  basin,
  countertop,
  floorY,
  cabinetHeight,
  counterThickness,
  addedBy,
}) {
  if (!basin || !countertop) return null;
  return createSinkFaucet({
    sinkPos: { x: basin.center.x, z: basin.center.z },
    sinkId: basin.id,
    sinkType: basin.sinkType,
    countertopId: countertop.id,
    countertopSurfaceY: floorY + cabinetHeight + counterThickness,
    // Contain the faucet body on the counter — same rule as assembleVanity.
    countertop: {
      center: { x: countertop.center.x, z: countertop.center.z },
      depth: countertop.dimensions.z,
    },
    rotation: basin.rotation,
    fixtureGroupId: basin.fixtureGroupId,
    faucetId: id,
    label: 'Faucet',
    addedBy,
  });
}

module.exports = { buildVanitySinkBasin, buildVanityFaucet };
